import React, { useState, useEffect } from 'react';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import getColorCode from 'src/utils/GetThemeColor';
import 'src/style/syncevents.css';
import { useSelector, useDispatch } from 'react-redux';
import ToggleSwitch from 'src/pages/Settings/SyncEvents/ToggleSwitch';
import Table from 'src/pages/Settings/SyncEvents/Table';
import {
  getPullEventSyncLogs,
  getPushEventSyncLogs,
} from 'src/redux/Actions/SyncLog';
import EventSyncLogTableContent from 'src/pages/Settings/SyncEvents/EventSyncLogTableContent';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { useLocation } from 'react-router-dom';
import usePageTitle from 'src/utils/SetPageTitle';

function EventSyncLog({ eventObjectKey }) {
  usePageTitle('Cloud VT • Sync Events ');
  const dispatch = useDispatch();
  const location = useLocation();
  const authReducer = useSelector((state) => state.AuthReducer);

  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;
  const eventLogSyncReducer = useSelector((state) => state.eventLogSyncReducer);

  const eventTitleMap = {
    1: 'Account',
    2: 'Product',
    3: 'Purchase Order',
    4: 'Sales Order',
    5: 'Goods Received Note',
  };

  const eventTitle = eventTitleMap[eventObjectKey];

  // Separate state for each input field
  const [filters, setFilters] = useState({
    searchQuery: '',
    pageNumber: 1,
    tableSortOrder: null,
    tableSortField: null,
    startDate: '',
    endDate: '',
    syncType: 'push',
    resolved: false,
  });

  let tableHeaders =
      eventLogSyncReducer?.pushEventLogList?.selectedFields || [],
    syncLogs = eventLogSyncReducer?.pushEventLogList?.pushEventLogs || [],
    totalPages = eventLogSyncReducer?.pushEventLogList?.totalPages || 1;
  // Table headers definition
  if (filters.syncType === 'push') {
    // Push event log headers
    tableHeaders = eventLogSyncReducer?.pushEventLogList?.selectedFields || [];
    syncLogs = eventLogSyncReducer?.pushEventLogList?.pushEventLogs || [];
    totalPages = eventLogSyncReducer?.pushEventLogList?.totalPages || 1;
  } else {
    // Pull event log headers
    tableHeaders = eventLogSyncReducer?.pullEventLogList?.selectedFields || [];
    syncLogs = eventLogSyncReducer?.pullEventLogList?.pullEventLogs || [];
    totalPages = eventLogSyncReducer?.pullEventLogList?.totalPages || 1;
  }

  // Handle pagination
  const handleNextPage = () => {
    if (filters.pageNumber < totalPages) {
      setFilters((prev) => ({
        ...prev,
        pageNumber: prev.pageNumber + 1,
      }));
    }
  };

  const handlePreviousPage = () => {
    if (filters.pageNumber > 1) {
      setFilters((prev) => ({
        ...prev,
        pageNumber: prev.pageNumber - 1,
      }));
    }
  };

  const handlePageNumberChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setFilters((prev) => ({
        ...prev,
        pageNumber: value,
      }));
    }
  };

  const handleResolvedCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setFilters((prev) => ({
      ...prev,
      resolved: isChecked,
      pageNumber: 1,
    }));
  };

  const handleTableSortOrder = (value, field) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber: 1, // Reset page number when sorting
      tableSortOrder: value,
      tableSortField: field,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSyncTypeToggle = (value) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery: '',
      pageNumber: 1,
      tableSortOrder: null,
      tableSortField: null,
      startDate: '',
      endDate: '',
      syncType: value,
      resolved: false,
    })); // Reset filters when sync type changes
  };

  const fetchSyncLogs = (type) => {
    if (type === 'push') {
      dispatch(
        getPushEventSyncLogs(
          tenantOrganisationId,
          authReducer?.userData?.tenant?.id,
          eventObjectKey, // typeKeyId 1 for account sync, 2 for product sync, 3 for purchase order sync
          filters.pageNumber,
          filters.searchQuery,
          filters.tableSortOrder,
          filters.tableSortField,
          filters.startDate,
          filters.endDate,
          filters.resolved,
        ),
      );
    } else {
      dispatch(
        getPullEventSyncLogs(
          tenantOrganisationId,
          authReducer.userData.tenant.id,
          eventObjectKey, // typeKeyId 1 for account sync, 2 for product sync, 3 for purchase order sync
          filters.pageNumber,
          filters.searchQuery,
          filters.tableSortOrder,
          filters.tableSortField,
          filters.startDate,
          filters.endDate,
          filters.resolved,
        ),
      );
    }
  };

  // Fetch logs when component mounts or when filters/pagination change
  useEffect(() => {
    fetchSyncLogs(filters.syncType);
  }, [filters]);

  useSetBreadcrumbData(location.pathname, eventTitle);

  return (
    <div className="page-layout div-flex-column">
      <div className="div-space-between div-flex-column border-bottom">
        <div className="div-flex-row cg-5 left-padding padding-bottom-8">
          <Breadcrumb />
        </div>
        <div className="slider-padding">
          <span className="text-xxl text-semi-bold left-padding">
            {eventTitle}
          </span>
        </div>
      </div>
      <div className="setting-main-container div-flex-column">
        <div className="div-flex-row justify-content-between align-items-center mb-3">
          <div className="div-flex-row align-items-center gap-2">
            <div>
              <span className="text-sm">
                Search by {tableHeaders[0]?.label}
              </span>
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  name="searchQuery"
                  className="form-input-section input-icon-remove"
                  placeholder={`Search by ${tableHeaders[0]?.label}`}
                  value={filters.searchQuery}
                  onChange={handleInputChange}
                />
                <span className="form-input-section-icon">
                  <Search
                    className="icon-size-lg"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm">Start Date</span>
              <div className="filter-input-wrapper">
                <input
                  type="date"
                  name="startDate"
                  className="form-input-section"
                  value={filters.startDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div>
              <span className="text-sm">End Date</span>
              <div className="filter-input-wrapper">
                <input
                  type="date"
                  name="endDate"
                  className="form-input-section"
                  value={filters.endDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
          <div className="toggle-area">
            <div className="div-flex-row cg-10 div-align-center text-md p-2">
              <div className="flex items-center space-x-2">
                <input
                  id="resolved-checkbox"
                  type="checkbox"
                  checked={filters.resolved}
                  onChange={handleResolvedCheckboxChange}
                />
                {'  '}
                <label htmlFor="resolved-checkbox">Resolved</label>
              </div>
              <ToggleSwitch
                value={filters.syncType}
                onChange={handleSyncTypeToggle}
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <Table
            headers={tableHeaders}
            minCellWidth={150}
            tableContent={
              <EventSyncLogTableContent
                headers={tableHeaders}
                syncLogs={syncLogs}
              />
            }
            tableDataSort={filters.tableSortOrder}
            handleTableSortOrder={handleTableSortOrder}
          />
        </div>
      </div>

      {/* Pagination */}
      <div className="footer-container">
        <div className="div-space-between div-align-center footer-content-for-page">
          <div className="div-flex-row cg-20 pagination-container div-align-center">
            <div className="div-flex-row cg-10 div-align-center">
              <button
                className="icon-btn-none"
                onClick={handlePreviousPage}
                disabled={filters.pageNumber === 1}
                aria-label="Previous page"
              >
                <ArrowLeft
                  className="icon-size-sm"
                  color={getColorCode(themeColor)}
                />
              </button>
              <input
                className="page-number-container input-icon-remove text-sm"
                value={filters.pageNumber}
                onChange={handlePageNumberChange}
                type="number"
                max={totalPages}
              />
              <span className="text-sm">of {totalPages}</span>
              <button
                className="icon-btn-none"
                onClick={handleNextPage}
                disabled={filters.pageNumber === totalPages}
                aria-label="Next page"
              >
                <ArrowRight
                  className="icon-size-sm"
                  color={getColorCode(themeColor)}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventSyncLog;
